import { useState, useContext, useEffect } from 'react';
import { toast } from 'sonner';
import { UserContext } from '~/contexts/UserContext';
import { PrepApi } from '~/api/prep';

/**
 * Custom hook to manage all state and business logic for the PrepPalletModal
 * @param {object} palletData - Data for the pallet being prepped
 * @param {array} scrapReasons - Array of scrap reason options
 * @param {function} onOpenChange - Function to handle modal open/close state changes
 * @param {boolean} isOpen - Whether the modal is open
 * @param {function} onSuccess - Function to call when prep is successful
 * @returns {object} All state and handlers needed by the modal component
 */
export function usePrepPalletModal(palletData, scrapReasons, onOpenChange, isOpen, onSuccess) {
    const { user } = useContext(UserContext);
    const prepApi = new PrepApi();

    // Initial form data
    const initialFormData = {
        numRPCs: '',
        rpcsPerPallet: '',
        unitsPerRPC: '',
        looseUnits: '',
        scrapUnits: '',
        scrapReason: scrapReasons?.[0]?.value,
        moveToLocation: '',
        moveToLocationName: '',
        remainingCases: '',
        remainingUnits: '',
    };

    // State management
    const [currentStep, setCurrentStep] = useState(1);
    const [formData, setFormData] = useState(initialFormData);
    const [isStepValid, setIsStepValid] = useState(false);
    const [confirmationRequired, setConfirmationRequired] = useState(false);
    const [showVarianceConfirmation, setShowVarianceConfirmation] = useState(false);
    const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);
    const [varianceDetails, setVarianceDetails] = useState({ variance: '0.0%', expected: 0, userDeclaredTotal: 0 });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [newLotId, setNewLotId] = useState(null);
    const [newPallets, setNewPallets] = useState([]);
    const [destinationLocations, setDestinationLocations] = useState([]);

    // Fetch destination locations when modal opens
    useEffect(() => {
        const fetchDestinationLocations = async () => {
            const response = await prepApi.getDestinationLocations({
                facilityId: user.facilityID,
                originLocationId: palletData.locationId,
            });
            if (!response.status || !response?.data?.rows?.length) {
                toast.error(response.msg || 'Failed to fetch destination locations.');
                return;
            }

            setDestinationLocations(
                response?.data?.rows?.map((location) => ({ value: location.locationId, label: location.locationName })),
            );
        };

        if (palletData?.locationId && isOpen) {
            fetchDestinationLocations();
        }
    }, [palletData?.locationId, isOpen]);

    // Set default move to location when destination locations are fetched
    useEffect(() => {
        const defaultLocation = destinationLocations?.[0];
        if (defaultLocation) {
            setFormData((prev) => ({
                ...prev,
                moveToLocation: defaultLocation.value,
                moveToLocationName: defaultLocation.label,
            }));
        }
    }, [destinationLocations]);

    // Reset modal state
    const resetModalState = () => {
        setCurrentStep(1);
        setFormData(initialFormData);
        setIsStepValid(false);
        setConfirmationRequired(false);
        setShowVarianceConfirmation(false);
        setShowCloseConfirmation(false);
        setNewLotId(null);
        setNewPallets([]);
    };

    // Determine if we should show confirmation when user tries to close
    const shouldShowCloseConfirmation = () => {
        // Step 4: Allow normal closing (prep is complete)
        if (currentStep === 4) return false;

        // Step 1: Show confirmation if user has entered any data (excluding auto-set fields)
        if (currentStep === 1) {
            const fieldsToCheck = {
                numRPCs: formData.numRPCs,
                rpcsPerPallet: formData.rpcsPerPallet,
                unitsPerRPC: formData.unitsPerRPC,
                looseUnits: formData.looseUnits,
                scrapUnits: formData.scrapUnits,
            };
            return Object.values(fieldsToCheck).some((value) => value !== '');
        }

        // Steps 2 and 3: Always show confirmation
        return currentStep === 2 || currentStep === 3;
    };

    // Navigation handlers
    const proceedToNextStep = () => {
        setShowVarianceConfirmation(false);
        setCurrentStep((prev) => prev + 1);
    };

    const handleNext = () => {
        if (confirmationRequired && currentStep === 2) {
            setShowVarianceConfirmation(true);
        } else {
            proceedToNextStep();
        }
    };

    const handleBack = () => {
        setCurrentStep((prev) => prev - 1);
    };

    // API call to confirm prep
    const handleConfirmPrep = async () => {
        setIsSubmitting(true);
        try {
            const response = await prepApi.prepPallet({
                palletID: palletData.palletId,
                lotID: palletData.lotId,
                locationID: palletData.locationId,
                numRPCs: formData.numRPCs,
                rpcsPerPallet: formData.rpcsPerPallet,
                unitsPerRPC: formData.unitsPerRPC,
                looseUnits: formData.looseUnits || 0,
                scrapUnits: formData.scrapUnits || 0,
                scrapReason: formData.scrapReason,
                destinationLocationID: formData.moveToLocation,
                casesRemaining: formData.remainingCases,
                unitsRemaining: formData.remainingUnits,
            });

            if (response.status) {
                setNewLotId(response.data.newLotId);
                // Preferred: backend provides per-pallet breakdown (ids + counts).
                const responseNewPallets = Array.isArray(response?.data?.newPallets) ? response.data.newPallets : [];
                setNewPallets(responseNewPallets);
                proceedToNextStep();
                if (onSuccess instanceof Function) {
                    onSuccess();
                }
            } else {
                toast.error(response.msg || 'Failed to complete prep.');
            }
        } catch (error) {
            toast.error(error.message || 'An unexpected error occurred.');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Modal close handlers
    const handleOpenChange = (open) => {
        if (!open && shouldShowCloseConfirmation()) {
            // Don't close yet, show confirmation instead
            setShowCloseConfirmation(true);
        } else {
            // Safe to close or user confirmed
            resetModalState();
            onOpenChange(open);
        }
    };

    const handleCloseConfirm = () => {
        // Reset all state and close directly
        resetModalState();
        onOpenChange(false);
    };

    // Return all state and handlers needed by the component
    return {
        // State
        currentStep,
        formData,
        isStepValid,
        confirmationRequired,
        showVarianceConfirmation,
        showCloseConfirmation,
        varianceDetails,
        isSubmitting,
        newLotId,
        newPallets,
        destinationLocations,

        // State setters (for child components)
        setFormData,
        setIsStepValid,
        setConfirmationRequired,
        setVarianceDetails,
        setShowVarianceConfirmation,
        setShowCloseConfirmation,

        // Handlers
        handleNext,
        handleBack,
        handleConfirmPrep,
        handleOpenChange,
        handleCloseConfirm,
        proceedToNextStep,
        shouldShowCloseConfirmation,
    };
}
